import React, { Component } from 'react';
import _ from 'lodash';
import { connect } from 'react-redux';
import mixpanel from '../../utils/mixpanel';
import { toastIfError } from '../../utils/toast';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router';
import { EmvioCard, EmvioLoadingSpinner as LoadingSpinner, EmvioRow, EmvioTwoColumnItem } from '@nexio/emvio-util-ui';
import { getWebhookLog as queryForWebhookLog, resendWebhookLogs } from './webhookLogActions';
import {
    getIsWebhookLogLoading,
    getIsWebhookResendLoading,
    getWebhookLog,
    getWebhookLogErrorMessage,
    getWebhookResend,
    getWebhookResendErrorMessage
} from './webhookLogEntity';
import { doesPropExistAndHasItChanged, prettifyMoney } from '@nexio/emvio-util-app';
import { prettifyDatetimeNoTimeZone } from '../../utils/string';
import './WebhookLogEntry.scss';
import { getTransactionsPaymentIdLink } from '../Transactions/transactionsActions';
import { EmvioButton } from '@nexio/emvio-form-components';
import { toast } from 'react-toastify';

export class WebhookLogEntry extends Component {
    static propTypes = {
        onGetWebhookLogById: PropTypes.func,
        resendWebhookLog: PropTypes.func,

        isWebhookLogLoading: PropTypes.bool,
        webhookLog: PropTypes.any,
        error: PropTypes.string,

        isWebhookResendLoading: PropTypes.bool,
        isWebhookResendSuccess: PropTypes.bool,
        resendError: PropTypes.string,

        match: PropTypes.shape({
            isExact: PropTypes.bool,
            params: PropTypes.shape({
                logId: PropTypes.string
            }),
            path: PropTypes.string,
            url: PropTypes.string
        })

    };

    constructor(props) {
        super(props);

        this.state = {};
    }

    componentDidMount() {
        this.props.onGetWebhookLogById(this.props.match.params.logId);
        mixpanel.track('Webhook page mounted');
    }

    componentDidUpdate(prevProps) {
        toastIfError(prevProps, this.props);
        toastIfError(prevProps, this.props, 'resendError');

        if (doesPropExistAndHasItChanged(prevProps, this.props, 'isWebhookResendSuccess')) {
            toast('Webhook Resent', { type: 'success' });
        }
    }

    handleResendWebhook = () => {
        this.props.resendWebhookLog({ logId: this.props.match.params.logId });
    };

    renderCard(items, headerTitle, oneColumnItem) {
        if (items.some((item) => (item.value))) {
            const classes = ['grow-height'];
            if (!oneColumnItem) {
                classes.push('half-width');
            }

            return (
                <EmvioCard className={classes.join(' ')} HeaderComponent={headerTitle}>
                    {items.map((item) =>
                        (<EmvioTwoColumnItem
                            key={item.label}
                            alterMethod={item.alterMethod}
                            label={item.label}
                            value={item.value}
                            breakAll={item.breakAll}
                        />)
                    )}
                </EmvioCard>);
        } else {
            return null;
        }
    }

    renderStatus(oneColumnItem) {
        const webhookLog = this.props.webhookLog;

        const items = [
            {
                label: 'Date',
                value: _.get(webhookLog, 'dateCreated'),
                alterMethod: (data) => prettifyDatetimeNoTimeZone(data)
            },
            {
                label: 'Event Type',
                value: _.get(webhookLog, 'eventType')
            },
            {
                label: 'Webhook URL',
                value: _.get(webhookLog, 'url') || 'Webhook not configured'
            },
            {
                label: 'Webhook Status',
                value: _.get(webhookLog, 'response.status'),
                alterMethod: (data) => data || 'Not Configured'
            }
        ];

        if (webhookLog.merchantId) {
            items.push({
                label: 'Merchant Id',
                value: _.get(webhookLog, 'merchantId')
            });
        } else if (webhookLog.payoutAccountId) {
            items.push({
                label: 'Payout Account Id',
                value: _.get(webhookLog, 'payoutAccountId')
            });
        }

        return this.renderCard(items, 'Webhook', oneColumnItem);
    }

    renderTransaction() {
        const webhookLog = this.props.webhookLog;
        const paymentId = _.get(webhookLog, 'requestBody.id');

        const items = [
            {
                label: 'Transaction Date',
                value: _.get(webhookLog, 'transactionDate'),
                alterMethod: (data) => prettifyDatetimeNoTimeZone(data)
            },
            {
                label: 'Amount',
                value: _.get(webhookLog, 'requestBody.data.amount'),
                alterMethod: ((amount) => prettifyMoney(amount, _.get(webhookLog, 'requestBody.data.currency')))
            },
            {
                label: 'Currency',
                value: _.get(webhookLog, 'requestBody.data.currency')
            },
            {
                label: 'Gateway',
                value: _.get(webhookLog, 'gatewayType')
            },
            {
                label: 'Ref Num',
                value: _.get(webhookLog, 'refNumber')
            },
            {
                label: 'Auth Code',
                value: _.get(webhookLog, 'requestBody.authCode')
            },
            {
                label: 'Card Number',
                value: _.get(webhookLog, 'requestBody.card.cardNumber')
            },
            {
                label: 'Payment Id',
                value: paymentId ? <a href={getTransactionsPaymentIdLink(paymentId)}>{paymentId}</a> : null
            },
            {
                label: 'Order Number',
                value: _.get(webhookLog, 'transactionDetails.orderId')
            }
        ];

        return this.renderCard(items, 'Transaction');
    }

    renderRequest() {
        const webhookLog = this.props.webhookLog;

        return (
            <EmvioCard className="half-width grow-height" HeaderComponent="Raw Request">
                <pre className="json-code">{JSON.stringify(webhookLog.requestBody, null, 2)}</pre>
            </EmvioCard>
        );
    }

    renderResponse() {
        const webhookLog = this.props.webhookLog;

        let response = webhookLog.response;
        if (_.isObject(response)) {
            response = JSON.stringify(response, null, 2);
        }

        return (
            <EmvioCard className="half-width grow-height" HeaderComponent="Raw Response">
                <pre className="json-code">{response}</pre>
            </EmvioCard>
        );
    }

    render() {
        if (this.props.isWebhookLogLoading) {
            return (<LoadingSpinner />);
        }

        if (_.isEmpty(this.props.webhookLog) || this.props.error) {
            return null;
        }

        const transactionCard = this.renderTransaction();
        const statusCard = this.renderStatus(!transactionCard);

        const requestCard = this.renderRequest();
        const responseCard = this.renderResponse();

        return (
            <div className="emvio-webhook-log-entry">
                <EmvioRow className="actions">
                    <EmvioButton
                        className="action-button resend-btn"
                        invertColors
                        isLoading={this.props.isWebhookResendLoading}
                        onClick={this.handleResendWebhook}
                        size="small"
                    >
                        Resend
                    </EmvioButton>
                </EmvioRow>
                <EmvioRow>
                    {statusCard}
                    {transactionCard}
                </EmvioRow>
                <EmvioRow>
                    {requestCard}
                    {responseCard}
                </EmvioRow>
            </div>
        );
    }
}

function mapStateToProps(state) {
    return {
        error: getWebhookLogErrorMessage(state),
        isWebhookLogLoading: getIsWebhookLogLoading(state),
        webhookLog: getWebhookLog(state),
        resendError: getWebhookResendErrorMessage(state),
        isWebhookResendLoading: getIsWebhookResendLoading(state),
        isWebhookResendSuccess: getWebhookResend(state)
    };
}

function mapDispatchToProps(dispatch) {
    return {
        onGetWebhookLogById: (logId) => dispatch(queryForWebhookLog(logId)),
        resendWebhookLog: (webhook) => dispatch(resendWebhookLogs([webhook]))
    };
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(WebhookLogEntry));
